import { ref } from 'vue'
import { defineStore } from 'pinia'
import type { MenuItem } from 'primevue/menuitem'
import { useRouter } from 'vue-router'

const STORAGE_KEY = 'mop-breadcrumb-items'

export const useBreadcrumbStore = defineStore('breadcrumb', () => {
  const router = useRouter()
  // Initialize from localStorage if available
  const storedItems = localStorage.getItem(STORAGE_KEY)
  const breadcrumbItems = ref<MenuItem[]>([])

  // Function to add command to menu item
  const addCommandToItem = (item: MenuItem): MenuItem => {
    return {
      ...item,
      command: (event: { originalEvent?: Event }) => {
        if (item.path === '/accounts') {
          event.originalEvent?.preventDefault()
          router.push({
            path: '/accounts/dashboard',
            query: item.query || {}
          })
        } else if (item.path) {
          router.push({
            path: item.path,
            query: item.query || {}
          })
        }
      }
    }
  }

  // Load stored items and add command function
  if (storedItems) {
    const parsedItems = JSON.parse(storedItems)
    breadcrumbItems.value = parsedItems.map(addCommandToItem)
  }

  const setBreadcrumbItems = (items: MenuItem[]) => {
    if (breadcrumbItems.value.length === 0) {
      // If no existing items, set the entire array with command functions
      breadcrumbItems.value = items.map(addCommandToItem)
    } else {
      // Get the last item from the new items array
      const lastItem = items[items.length - 1]
      if (!lastItem) return

      // Find if the last item exists in current breadcrumbs
      const existingIndex = breadcrumbItems.value.findIndex(item => item.label === lastItem.label)

      if (existingIndex !== -1) {
        // If found, remove all items after this index
        breadcrumbItems.value = breadcrumbItems.value.slice(0, existingIndex + 1).map(addCommandToItem)
      } else {
        // If not found, append the new item
        breadcrumbItems.value.push(addCommandToItem(lastItem))
      }
    }

    // Save to localStorage after updating, but remove command function
    const itemsToStore = items.map(({ label, path, query }) => ({ label, path, query }))
    localStorage.setItem(STORAGE_KEY, JSON.stringify(itemsToStore))
  }

  const clearBreadcrumbItems = () => {
    breadcrumbItems.value = []
    localStorage.removeItem(STORAGE_KEY)
  }

  return {
    breadcrumbItems,
    setBreadcrumbItems,
    clearBreadcrumbItems
  }
})
